import { Row, Col, Button, Form } from 'react-bootstrap';
import AppLoader from 'components/general/AppLoader';
import { useSelector } from "react-redux";
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
// import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getSocket } from '../../socket';
import { toast } from 'react-toastify';

import AgendaItemCard from './AgendaItemCard';
import AgendaItemAddNewModal from './crudModals/AgendaItemAddNewModal';
import AgendaItemModal from './modals/AgendaItemModal';
import { useCompactPortrait, useNavigationAction } from 'components/navigation/NavigationContext';
import { isAssignedSpeechEvaluator } from '_helpers/speechEvaluator';
import ParticipationModal from './ParticipationModal';
import TableTopicsReviewForm from './tableTopics/TableTopicsReviewForm';
// import noSleep from 'nosleep.js';
// import { toast } from 'react-toastify';
// const date = require('date-and-time');

const createSubmissionId = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();

  const values = new Uint32Array(4);
  window.crypto.getRandomValues(values);
  return Array.from(values, value => value.toString(16).padStart(8, '0')).join('');
};


export default function AgendaComponent({
  meeting,
  getMeeting,
  isLoading,
  editMode,
  showClockText = true,
  assignments,
  tableTopics = null,
  onOpenTableTopicsResults = null,
}) {
  const EVALUATION_WINDOW_MS = 3 * 60 * 60 * 1000;
  const compact = useCompactPortrait();
  const FEEDBACK_WINDOW_MS = 2 * 60 * 60 * 1000;
  // State for poll modal
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [activePoll, setActivePoll] = useState(null);
  const [feedbackPrompts, setFeedbackPrompts] = useState([]);
  const [feedbackPrompt, setFeedbackPrompt] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const [feedbackSavedByAttempt, setFeedbackSavedByAttempt] = useState({});
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [evaluationPrompt, setEvaluationPrompt] = useState(null);
  const [showEvaluationModal, setShowEvaluationModal] = useState(false);
  const [showTableTopicsModal, setShowTableTopicsModal] = useState(false);
  const [managedEvaluationTopics, setManagedEvaluationTopics] = useState([]);
  const [evaluationTopics, setEvaluationTopics] = useState([]);
  const [evaluationSavedByAttempt, setEvaluationSavedByAttempt] = useState({});
  const [evaluationSubmitting, setEvaluationSubmitting] = useState(false);
  const [evaluationEvaluatorName, setEvaluationEvaluatorName] = useState('');
  const [evaluationFeedbackText, setEvaluationFeedbackText] = useState('');
  const [feedbackExpiredByKey, setFeedbackExpiredByKey] = useState({});
  const feedbackHideTimeoutRef = useRef({});
  const feedbackExpireTimeoutsRef = useRef({});
  const feedbackPromptsRef = useRef([]);
  const feedbackPromptRef = useRef(null);
  const evaluationPromptRef = useRef(null);
  const loadAttemptFeedbacksRef = useRef(null);
  const loadAttemptEvaluationsRef = useRef(null);
  const schedulePromptHideRef = useRef(null);
  const scheduleFeedbackExpireRef = useRef(null);
  const writeStoredFeedbackPromptsRef = useRef(null);
  const removePromptByKeyRef = useRef(null);
  const feedbackPromptAgendaItemIdRef = useRef(undefined);
  const feedbackSubmissionFallbackRef = useRef({});
  const evaluationSubmissionFallbackRef = useRef({});
  // open and setOpen are now controlled by parent
  // Remove local state, use props
  const { user: authUser } = useSelector(x => x.auth);
  const url = process.env.REACT_APP_URL;
  const params = useParams();
  const meetingId = params.meetingId;
  const feedbackPromptStorageKey = `meeting-feedback-prompts:${meetingId || 'unknown'}`;
  const agenda = meeting.items;

  const readStoredFeedbackSubmission = useCallback((attemptId) => {
    if (!attemptId) return null;

    const fallback = feedbackSubmissionFallbackRef.current[attemptId] || null;
    try {
      const raw = window.localStorage.getItem(`meeting-feedback-submission:${meetingId}:${attemptId}`);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return parsed?.submissionId && typeof parsed?.text === 'string' ? parsed : fallback;
    } catch {
      return fallback;
    }
  }, [meetingId]);

  const writeStoredFeedbackSubmission = useCallback((attemptId, submission) => {
    if (!attemptId || !submission?.submissionId) return;

    feedbackSubmissionFallbackRef.current[attemptId] = submission;
    try {
      window.localStorage.setItem(
        `meeting-feedback-submission:${meetingId}:${attemptId}`,
        JSON.stringify(submission)
      );
    } catch {
      // Keep the in-memory fallback when browser storage is unavailable.
    }
  }, [meetingId]);

  const readStoredEvaluationSubmission = useCallback((attemptId) => {
    if (!attemptId) return null;

    const fallback = evaluationSubmissionFallbackRef.current[attemptId] || null;
    try {
      const raw = window.localStorage.getItem(`meeting-evaluation-submission:${meetingId}:${attemptId}`);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return parsed?.submissionId
        && typeof parsed?.evaluatorName === 'string'
        && Array.isArray(parsed?.topics)
        ? parsed
        : fallback;
    } catch {
      return fallback;
    }
  }, [meetingId]);

  const writeStoredEvaluationSubmission = useCallback((attemptId, submission) => {
    if (!attemptId || !submission?.submissionId) return;

    evaluationSubmissionFallbackRef.current[attemptId] = submission;
    try {
      window.localStorage.setItem(
        `meeting-evaluation-submission:${meetingId}:${attemptId}`,
        JSON.stringify(submission)
      );
    } catch {
      // Keep the in-memory fallback when browser storage is unavailable.
    }
  }, [meetingId]);

  // let myNoSleep = new noSleep();

  // Toolbar state now managed in Meeting.js
  const [agendaWithStartTimes, setAgendaWithStartTimes] = useState([]);

  // NoSleep logic now handled in Meeting.js

  // Toolbar handler now managed in Meeting.js

  const calculateStartTime = useCallback((meetingTime, agenda, meetingTheme) => {
    let startTime = meetingTime; // Initialize with the meeting's start time (e.g., "18:30")
    const meetingNumber = meeting.meetingDetails?.meetingNumber;
    return agenda.map((agendaItem, index) => {
      if (index === 0) {
        // The first item starts at the meeting's start time
        return { ...agendaItem, startTime: meetingTime, meetingTheme, meetingNumber };
      } else {
        // Calculate the start time for subsequent items
        const previousItem = agenda[index - 1];
        const previousDuration = previousItem.agendaEvent?.total || "00:00"; // Default to "00:00" if no duration
        const [addedMinutes, addedSeconds] = previousDuration.split(':').map(Number);

        // Parse the current start time (HH:mm)
        const [currentHours, currentMinutes] = startTime.split(':').map(Number);

        // Create a new Date object to handle time calculations
        const newStartTime = new Date();
        newStartTime.setHours(currentHours);
        newStartTime.setMinutes(currentMinutes + addedMinutes); // Add minutes
        newStartTime.setSeconds(addedSeconds || 0); // Add seconds (default to 0 if not provided)

        // Format the new start time as "HH:mm"
        const formattedStartTime = `${String(newStartTime.getHours()).padStart(2, '0')}:${String(newStartTime.getMinutes()).padStart(2, '0')}`;
        startTime = formattedStartTime; // Update the start time for the next item

        return { ...agendaItem, startTime: formattedStartTime, meetingTheme, meetingNumber };
      }
    });
  }, [meeting.meetingDetails?.meetingNumber]);

  const loadAttemptFeedbacks = useCallback(async (prompt, forModal = false) => {
    if (!prompt?.userId || !prompt?.userPathwayId || !prompt?.projectId || !prompt?.attemptId) {
      if (forModal) {
        setFeedbackText('');
        setFeedbackSaved(false);
      }
      return;
    }

    const storedSubmission = readStoredFeedbackSubmission(prompt.attemptId);
    if (storedSubmission) {
      setFeedbackSavedByAttempt(prev => ({
        ...prev,
        [prompt.attemptId]: true,
      }));
      if (forModal) {
        setFeedbackText(storedSubmission.text);
        setFeedbackSaved(true);
      }
      return;
    }

    try {
      const response = await fetch(
        `${url}/user/${prompt.userId}/userPathway/${prompt.userPathwayId}/project/${prompt.projectId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser?.token || ''}`,
          },
        }
      );

      if (!response.ok) {
        if (forModal) {
          setFeedbackText('');
          setFeedbackSaved(false);
        }
        return;
      }

      const userProject = await response.json();
      const attempts = Array.isArray(userProject?.attempts) ? userProject.attempts : [];
      const attempt = attempts.find(entry => String(entry?._id) === String(prompt.attemptId));
      const entries = Array.isArray(attempt?.feedbacks) ? attempt.feedbacks : [];
      const currentUserId = String(authUser?.user?._id || '').trim();
      const currentUserName = `${authUser?.user?.firstName || ''} ${authUser?.user?.lastName || ''}`.trim().toLowerCase();
      const myFeedback = entries.find((entry) => {
        const authorId = String(entry?.authorId || '').trim();
        const authorName = String(entry?.authorName || '').trim().toLowerCase();

        if (currentUserId && authorId) {
          return authorId === currentUserId;
        }

        if (!currentUserId && currentUserName && authorName) {
          return authorName === currentUserName;
        }

        return false;
      }) || null;
      const isSaved = Boolean(myFeedback);

      setFeedbackSavedByAttempt(prev => ({
        ...prev,
        [prompt.attemptId]: isSaved,
      }));

      if (forModal) {
        setFeedbackText(myFeedback?.description || myFeedback?.text || '');
        setFeedbackSaved(isSaved);
      }
    } catch (err) {
      if (forModal) {
        setFeedbackText('');
        setFeedbackSaved(false);
      }
    }
  }, [authUser?.token, authUser?.user?._id, authUser?.user?.firstName, authUser?.user?.lastName, readStoredFeedbackSubmission, url]);

  const loadAttemptEvaluations = useCallback(async (prompt, forModal = false) => {
    if (!prompt?.userId || !prompt?.userPathwayId || !prompt?.projectId || !prompt?.attemptId) {
      if (forModal) {
        const fallbackTopics = managedEvaluationTopics.map((topicName) => ({
          name: topicName,
          stars: 0,
          shortComment: '',
        }));
        setEvaluationTopics(fallbackTopics);
        setEvaluationFeedbackText('');
      }
      return;
    }

    const storedSubmission = readStoredEvaluationSubmission(prompt.attemptId);
    if (storedSubmission) {
      setEvaluationSavedByAttempt(prev => ({
        ...prev,
        [prompt.attemptId]: true,
      }));
      if (forModal) {
        setEvaluationEvaluatorName(storedSubmission.evaluatorName);
        setEvaluationTopics(storedSubmission.topics);
        setEvaluationFeedbackText(storedSubmission.feedback || '');
      }
      return;
    }

    try {
      const response = await fetch(
        `${url}/user/${prompt.userId}/userPathway/${prompt.userPathwayId}/project/${prompt.projectId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser?.token || ''}`,
          },
        }
      );

      if (!response.ok) {
        if (forModal) {
          const fallbackTopics = managedEvaluationTopics.map((topicName) => ({
            name: topicName,
            stars: 0,
            shortComment: '',
          }));
          setEvaluationTopics(fallbackTopics);
          setEvaluationFeedbackText('');
        }
        return;
      }

      const userProject = await response.json();
      const attempts = Array.isArray(userProject?.attempts) ? userProject.attempts : [];
      const attempt = attempts.find(entry => String(entry?._id) === String(prompt.attemptId));
      const evaluations = Array.isArray(attempt?.evaluations) ? attempt.evaluations : [];

      const currentUserId = String(authUser?.user?._id || '').trim();
      const currentUserName = `${authUser?.user?.firstName || ''} ${authUser?.user?.lastName || ''}`.trim().toLowerCase();
      const myEvaluation = evaluations.find((entry) => {
        const evaluatorId = String(entry?.evaluatorId || '').trim();
        const evaluatorName = String(entry?.evaluatorName || '').trim().toLowerCase();

        if (currentUserId && evaluatorId) {
          return evaluatorId === currentUserId;
        }

        if (!currentUserId && currentUserName && evaluatorName) {
          return evaluatorName === currentUserName;
        }

        return false;
      }) || null;

      setEvaluationSavedByAttempt(prev => ({
        ...prev,
        [prompt.attemptId]: Boolean(myEvaluation),
      }));

      if (forModal) {
        const savedTopicsByName = new Map(
          (Array.isArray(myEvaluation?.topics) ? myEvaluation.topics : []).map((topic) => [
            String(topic?.name || '').trim().toLowerCase(),
            topic,
          ])
        );

        const mergedTopics = managedEvaluationTopics.map((topicName) => {
          const savedTopic = savedTopicsByName.get(String(topicName || '').trim().toLowerCase());
          return {
            name: topicName,
            stars: Number(savedTopic?.stars || 0),
            shortComment: savedTopic?.shortComment || '',
          };
        });

        setEvaluationTopics(mergedTopics);
        setEvaluationFeedbackText(myEvaluation?.feedback || '');
      }
    } catch (err) {
      if (forModal) {
        const fallbackTopics = managedEvaluationTopics.map((topicName) => ({
          name: topicName,
          stars: 0,
          shortComment: '',
        }));
        setEvaluationTopics(fallbackTopics);
        setEvaluationFeedbackText('');
      }
    }
  }, [authUser?.token, authUser?.user?._id, authUser?.user?.firstName, authUser?.user?.lastName, managedEvaluationTopics, readStoredEvaluationSubmission, url]);

  const loadManagedEvaluationTopics = useCallback(async () => {
    try {
      const response = await fetch(`${url}/meetingsConfig`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        setManagedEvaluationTopics([]);
        return;
      }

      const config = await response.json();
      const topicNames = (Array.isArray(config?.evaluationTopics) ? config.evaluationTopics : [])
        .map((topic) => String(topic?.name || '').trim())
        .filter((name) => name.length > 0);
      setManagedEvaluationTopics(topicNames);
    } catch (err) {
      setManagedEvaluationTopics([]);
    }
  }, [url]);

  const refreshActivePoll = useCallback(async () => {
    if (!meetingId) return;

    try {
      const response = await fetch(`${url}/polls/${meetingId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) return;

      const polls = await response.json();
      const currentActivePoll = Array.isArray(polls)
        ? polls.find(poll => poll?.status === 'active')
        : null;

      if (!currentActivePoll) {
        setActivePoll(null);
        return;
      }

      setActivePoll({
        pollId: currentActivePoll._id,
        title: currentActivePoll.title || null,
      });
    } catch (err) {
      // no-op
    }
  }, [meetingId, url]);

  const readStoredFeedbackPrompts = useCallback(() => {
    if (!meetingId) return [];

    try {
      const raw = window.localStorage.getItem(feedbackPromptStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, [feedbackPromptStorageKey, meetingId]);

  const writeStoredFeedbackPrompts = useCallback((prompts) => {
    if (!meetingId) return;

    try {
      if (!Array.isArray(prompts) || prompts.length === 0) {
        window.localStorage.removeItem(feedbackPromptStorageKey);
        return;
      }

      window.localStorage.setItem(feedbackPromptStorageKey, JSON.stringify(prompts));
    } catch {
      // no-op
    }
  }, [feedbackPromptStorageKey, meetingId]);

  const removePromptByKey = useCallback((promptKey) => {
    if (!promptKey) return;

    setFeedbackPrompts(prev => {
      const next = prev.filter(entry => (entry?.attemptId || entry?.agendaItemId) !== promptKey);
      writeStoredFeedbackPrompts(next);
      return next;
    });
    setFeedbackSavedByAttempt(prev => {
      const next = { ...prev };
      delete next[promptKey];
      return next;
    });
    setEvaluationSavedByAttempt(prev => {
      const next = { ...prev };
      delete next[promptKey];
      return next;
    });
    setFeedbackExpiredByKey(prev => {
      const next = { ...prev };
      delete next[promptKey];
      return next;
    });

    if ((feedbackPrompt?.attemptId || feedbackPrompt?.agendaItemId) === promptKey) {
      setFeedbackPrompt(null);
      setShowFeedbackModal(false);
    }

    if ((evaluationPrompt?.attemptId || evaluationPrompt?.agendaItemId) === promptKey) {
      setEvaluationPrompt(null);
      setEvaluationFeedbackText('');
      setShowEvaluationModal(false);
    }

    if (feedbackHideTimeoutRef.current[promptKey]) {
      clearTimeout(feedbackHideTimeoutRef.current[promptKey]);
      delete feedbackHideTimeoutRef.current[promptKey];
    }

    if (feedbackExpireTimeoutsRef.current[promptKey]) {
      clearTimeout(feedbackExpireTimeoutsRef.current[promptKey]);
      delete feedbackExpireTimeoutsRef.current[promptKey];
    }
  }, [evaluationPrompt, feedbackPrompt, writeStoredFeedbackPrompts]);

  const schedulePromptHide = useCallback((promptKey, displayedAt) => {
    if (!promptKey) return;

    if (feedbackHideTimeoutRef.current[promptKey]) {
      clearTimeout(feedbackHideTimeoutRef.current[promptKey]);
      delete feedbackHideTimeoutRef.current[promptKey];
    }

    if (!displayedAt) return;

    const elapsedMs = Date.now() - Number(displayedAt);
    const remainingMs = Math.max(EVALUATION_WINDOW_MS - elapsedMs, 0);

    if (remainingMs <= 0) {
      removePromptByKey(promptKey);
      return;
    }

    feedbackHideTimeoutRef.current[promptKey] = setTimeout(() => {
      removePromptByKey(promptKey);
    }, remainingMs);
  }, [EVALUATION_WINDOW_MS, removePromptByKey]);

  const scheduleFeedbackExpire = useCallback((promptKey, displayedAt) => {
    if (!promptKey) return;

    if (feedbackExpireTimeoutsRef.current[promptKey]) {
      clearTimeout(feedbackExpireTimeoutsRef.current[promptKey]);
      delete feedbackExpireTimeoutsRef.current[promptKey];
    }

    if (!displayedAt) return;

    const elapsedMs = Date.now() - Number(displayedAt);
    const remainingMs = Math.max(FEEDBACK_WINDOW_MS - elapsedMs, 0);

    if (remainingMs <= 0) {
      setFeedbackExpiredByKey(prev => ({ ...prev, [promptKey]: true }));
      return;
    }

    feedbackExpireTimeoutsRef.current[promptKey] = setTimeout(() => {
      setFeedbackExpiredByKey(prev => ({ ...prev, [promptKey]: true }));
    }, remainingMs);
  }, [FEEDBACK_WINDOW_MS]);

  useEffect(() => {
    feedbackPromptsRef.current = feedbackPrompts;
  }, [feedbackPrompts]);

  useEffect(() => {
    feedbackPromptRef.current = feedbackPrompt;
    feedbackPromptAgendaItemIdRef.current = feedbackPrompt?.agendaItemId;
  }, [feedbackPrompt]);

  useEffect(() => {
    evaluationPromptRef.current = evaluationPrompt;
  }, [evaluationPrompt]);

  useEffect(() => {
    loadAttemptFeedbacksRef.current = loadAttemptFeedbacks;
  }, [loadAttemptFeedbacks]);

  useEffect(() => {
    loadAttemptEvaluationsRef.current = loadAttemptEvaluations;
  }, [loadAttemptEvaluations]);

  useEffect(() => {
    schedulePromptHideRef.current = schedulePromptHide;
  }, [schedulePromptHide]);

  useEffect(() => {
    scheduleFeedbackExpireRef.current = scheduleFeedbackExpire;
  }, [scheduleFeedbackExpire]);

  useEffect(() => {
    writeStoredFeedbackPromptsRef.current = writeStoredFeedbackPrompts;
  }, [writeStoredFeedbackPrompts]);

  useEffect(() => {
    removePromptByKeyRef.current = removePromptByKey;
  }, [removePromptByKey]);

  useEffect(() => {
    if (!meetingId) return;

    const nowMs = Date.now();
    const restoredPrompts = readStoredFeedbackPrompts()
      .filter((prompt) => {
        const promptKey = prompt?.attemptId || prompt?.agendaItemId;
        if (!promptKey) return false;
        if (!prompt?.attemptId || !prompt?.userId || !prompt?.userPathwayId || !prompt?.projectId) return false;
        const displayedAt = Number(prompt?.displayedAt || 0);
        return displayedAt > 0 && (nowMs - displayedAt) <= EVALUATION_WINDOW_MS;
      });

    setFeedbackPrompts(restoredPrompts);
    writeStoredFeedbackPrompts(restoredPrompts);

    restoredPrompts.forEach((prompt) => {
      const promptKey = prompt?.attemptId || prompt?.agendaItemId;
      const displayedAt = Number(prompt?.displayedAt || 0);
      loadAttemptFeedbacks(prompt, false);
      loadAttemptEvaluations(prompt, false);
      schedulePromptHide(promptKey, displayedAt);
      scheduleFeedbackExpire(promptKey, displayedAt);
      if (displayedAt > 0 && (nowMs - displayedAt) > FEEDBACK_WINDOW_MS) {
        setFeedbackExpiredByKey(prev => ({ ...prev, [promptKey]: true }));
      }
    });
  }, [EVALUATION_WINDOW_MS, FEEDBACK_WINDOW_MS, loadAttemptEvaluations, loadAttemptFeedbacks, meetingId, readStoredFeedbackPrompts, scheduleFeedbackExpire, schedulePromptHide, writeStoredFeedbackPrompts]);

  useEffect(() => {
    if (agenda?.length > 0) {
      const meetingTime = meeting.meetingDetails.meetingTime;
      const meetingTheme = meeting.meetingDetails.meetingTheme; // Extract meetingTheme
      setAgendaWithStartTimes(calculateStartTime(meetingTime, agenda, meetingTheme));
    }
  }, [agenda, meeting, calculateStartTime]);

  useEffect(() => {
    if (!meetingId) return undefined;

    const socket = getSocket();
    const joinMeetingRoom = () => {
      socket.emit('meeting:join', meetingId);
    };

    joinMeetingRoom();
    socket.on('connect', joinMeetingRoom);

    const handlePollStatusChanged = (payload) => {
      if (!payload || payload.meetingId !== meetingId) return;

      refreshActivePoll();
    };

    const handleFeedbackPrompt = (payload) => {
      if (!payload || payload.meetingId !== meetingId) return;

      // Ignore prompts that cannot be mapped to a specific speech attempt.
      if (!payload?.attemptId || !payload?.userId || !payload?.userPathwayId || !payload?.projectId) {
        return;
      }

      const promptKey = payload?.attemptId || payload?.agendaItemId;
      if (!promptKey) return;

      const displayedAt = Number(payload?.displayedAt || Date.now());

      // Reject if past evaluation window from speech start.
      if ((Date.now() - displayedAt) > EVALUATION_WINDOW_MS) {
        if (removePromptByKeyRef.current) removePromptByKeyRef.current(promptKey);
        return;
      }

      setFeedbackPrompts(prev => {
        const exists = prev.some(entry => (entry?.attemptId || entry?.agendaItemId) === promptKey);
        const normalized = { ...payload, displayedAt };
        const next = exists
          ? prev.map(entry => ((entry?.attemptId || entry?.agendaItemId) === promptKey ? normalized : entry))
          : [...prev, normalized];
        if (writeStoredFeedbackPromptsRef.current) writeStoredFeedbackPromptsRef.current(next);
        return next;
      });

      if (loadAttemptFeedbacksRef.current) loadAttemptFeedbacksRef.current(payload, false);
      if (loadAttemptEvaluationsRef.current) loadAttemptEvaluationsRef.current(payload, false);

      if (schedulePromptHideRef.current) schedulePromptHideRef.current(promptKey, displayedAt);
      if (scheduleFeedbackExpireRef.current) scheduleFeedbackExpireRef.current(promptKey, displayedAt);
    };

    // The speech moved to another pathway project: keep prompts, drafts and saved state attached to it.
    const handleFeedbackPromptRelink = (payload) => {
      if (!payload || payload.meetingId !== meetingId) return;

      const previousAttemptId = payload?.previousAttemptId;
      const nextAttemptId = payload?.attemptId;
      if (!previousAttemptId || !nextAttemptId || previousAttemptId === nextAttemptId) return;

      ['meeting-feedback-submission', 'meeting-evaluation-submission'].forEach((prefix) => {
        try {
          const previousKey = `${prefix}:${meetingId}:${previousAttemptId}`;
          const raw = window.localStorage.getItem(previousKey);
          if (!raw) return;
          window.localStorage.setItem(`${prefix}:${meetingId}:${nextAttemptId}`, raw);
          window.localStorage.removeItem(previousKey);
        } catch {
          // Browser storage is unavailable; in-memory fallbacks below still carry the submission.
        }
      });

      [feedbackSubmissionFallbackRef, evaluationSubmissionFallbackRef].forEach((ref) => {
        const stored = ref.current[previousAttemptId];
        if (!stored) return;
        ref.current[nextAttemptId] = stored;
        delete ref.current[previousAttemptId];
      });

      [feedbackHideTimeoutRef, feedbackExpireTimeoutsRef].forEach((ref) => {
        if (!ref.current[previousAttemptId]) return;
        clearTimeout(ref.current[previousAttemptId]);
        delete ref.current[previousAttemptId];
      });

      const remapAttemptKey = (prev) => {
        if (!(previousAttemptId in prev)) return prev;
        const next = { ...prev, [nextAttemptId]: prev[previousAttemptId] };
        delete next[previousAttemptId];
        return next;
      };
      setFeedbackSavedByAttempt(remapAttemptKey);
      setEvaluationSavedByAttempt(remapAttemptKey);
      setFeedbackExpiredByKey(remapAttemptKey);

      const relinkEntry = (entry) => (entry && entry.attemptId === previousAttemptId
        ? {
          ...entry,
          userId: payload.userId,
          userPathwayId: payload.userPathwayId,
          projectId: payload.projectId,
          attemptId: nextAttemptId,
        }
        : entry);

      setFeedbackPrompts(prev => {
        const next = prev.map(relinkEntry);
        if (writeStoredFeedbackPromptsRef.current) writeStoredFeedbackPromptsRef.current(next);
        return next;
      });
      setFeedbackPrompt(relinkEntry);
      setEvaluationPrompt(relinkEntry);
    };

    const handleFeedbackStop = (payload) => {
      if (!payload || payload.meetingId !== meetingId) return;

      const agendaItemId = payload?.agendaItemId;
      if (!agendaItemId) return;

      const stoppedAt = Number(payload?.stoppedAt || Date.now());

      // Update the matching prompt entry with stoppedAt
      setFeedbackPrompts(prev => {
        const next = prev.map(entry => {
          if (entry?.agendaItemId !== agendaItemId) return entry;
          return { ...entry, stoppedAt };
        });
        if (writeStoredFeedbackPromptsRef.current) writeStoredFeedbackPromptsRef.current(next);
        return next;
      });

      // Find the prompt key to schedule timers
      const target = feedbackPromptsRef.current.find(e => e?.agendaItemId === agendaItemId);
      const promptKey = target?.attemptId || target?.agendaItemId;
      const displayedAt = Number(target?.displayedAt || 0);
      if (promptKey) {
        if (schedulePromptHideRef.current) schedulePromptHideRef.current(promptKey, displayedAt);
        if (scheduleFeedbackExpireRef.current) scheduleFeedbackExpireRef.current(promptKey, displayedAt);
      }
    };

    socket.on('poll:status-changed', handlePollStatusChanged);
    socket.on('meeting:feedback-prompt', handleFeedbackPrompt);
    socket.on('meeting:feedback-prompt-relink', handleFeedbackPromptRelink);
    socket.on('meeting:feedback-stop', handleFeedbackStop);

    const handleFeedbackReset = (event) => {
      const detail = event?.detail || {};
      if (detail.meetingId !== meetingId) return;

      const targetAgendaItemId = detail.agendaItemId;
      const keysToClear = targetAgendaItemId
        ? feedbackPromptsRef.current
          .filter(entry => entry?.agendaItemId === targetAgendaItemId)
          .map(entry => entry?.attemptId || entry?.agendaItemId)
        : feedbackPromptsRef.current.map(entry => entry?.attemptId || entry?.agendaItemId);

      setFeedbackPrompts(prev => {
        const next = targetAgendaItemId
          ? prev.filter(entry => entry?.agendaItemId !== targetAgendaItemId)
          : [];
        if (writeStoredFeedbackPromptsRef.current) writeStoredFeedbackPromptsRef.current(next);
        return next;
      });

      if (!targetAgendaItemId || feedbackPromptAgendaItemIdRef.current === targetAgendaItemId) {
        setFeedbackPrompt(null);
        setFeedbackText('');
        setFeedbackSaved(false);
        setShowFeedbackModal(false);
        setEvaluationPrompt(null);
        setEvaluationTopics([]);
        setEvaluationFeedbackText('');
        setShowEvaluationModal(false);
      }

      setEvaluationSavedByAttempt(prev => {
        const next = { ...prev };
        keysToClear.forEach((key) => {
          delete next[key];
        });
        return next;
      });

      keysToClear.forEach((key) => {
        if (removePromptByKeyRef.current) removePromptByKeyRef.current(key);
      });
    };

    const handleSocketFeedbackReset = (payload) => {
      handleFeedbackReset({ detail: payload || {} });
    };

    socket.on('meeting:feedback-reset', handleSocketFeedbackReset);
    window.addEventListener('pathway-feedback-reset', handleFeedbackReset);

    return () => {
      socket.off('poll:status-changed', handlePollStatusChanged);
      socket.off('meeting:feedback-prompt', handleFeedbackPrompt);
      socket.off('meeting:feedback-prompt-relink', handleFeedbackPromptRelink);
      socket.off('meeting:feedback-stop', handleFeedbackStop);
      socket.off('meeting:feedback-reset', handleSocketFeedbackReset);
      socket.off('connect', joinMeetingRoom);
      window.removeEventListener('pathway-feedback-reset', handleFeedbackReset);
      socket.emit('meeting:leave', meetingId);
    };
  }, [meetingId, refreshActivePoll, EVALUATION_WINDOW_MS]);

  useEffect(() => {
    refreshActivePoll();
  }, [refreshActivePoll]);

  useEffect(() => {
    loadManagedEvaluationTopics();
  }, [loadManagedEvaluationTopics]);

  useEffect(() => {
    if (feedbackPrompt) return;
    if (showFeedbackModal) {
      setShowFeedbackModal(false);
    }
  }, [feedbackPrompt, showFeedbackModal]);

  useEffect(() => {
    if (!showEvaluationModal) return;
    if (evaluationTopics.length > 0) return;
    if (managedEvaluationTopics.length === 0) return;

    setEvaluationTopics(managedEvaluationTopics.map((topicName) => ({
      name: topicName,
      stars: 0,
      shortComment: '',
    })));
  }, [showEvaluationModal, evaluationTopics.length, managedEvaluationTopics]);

  const handleSubmitFeedback = useCallback(async () => {
    if (!feedbackPrompt?.attemptId) {
      toast.error('No active speech attempt to attach feedback to.', { position: 'bottom-left' });
      return;
    }

    const trimmedFeedback = feedbackText.trim();
    if (!trimmedFeedback) {
      toast.error('Please write some feedback before saving.', { position: 'bottom-left' });
      return;
    }

    setFeedbackSubmitting(true);
    try {
      const storedSubmission = readStoredFeedbackSubmission(feedbackPrompt.attemptId);
      const submissionId = storedSubmission?.submissionId || createSubmissionId();
      const nextFeedback = {
        submissionId,
        rating: 5,
        positives: [],
        negatives: [],
        description: trimmedFeedback,
        text: trimmedFeedback,
      };

      const response = await fetch(
        `${url}/user/${feedbackPrompt.userId}/userPathway/${feedbackPrompt.userPathwayId}/project/${feedbackPrompt.projectId}/attempt/${feedbackPrompt.attemptId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser?.token || ''}`,
          },
          body: JSON.stringify({ feedback: nextFeedback }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error || errorData?.Error || `Server error ${response.status}`);
      }

      writeStoredFeedbackSubmission(feedbackPrompt.attemptId, {
        submissionId,
        text: trimmedFeedback,
      });
      setFeedbackSaved(true);
      setFeedbackSavedByAttempt(prev => ({
        ...prev,
        [feedbackPrompt.attemptId]: true,
      }));
      setFeedbackText(trimmedFeedback);
      setShowFeedbackModal(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save feedback.', { position: 'bottom-left' });
    } finally {
      setFeedbackSubmitting(false);
    }
  }, [authUser?.token, feedbackPrompt, feedbackText, readStoredFeedbackSubmission, url, writeStoredFeedbackSubmission]);

  useEffect(() => {
    if (!feedbackPrompt) {
      setFeedbackText('');
      setFeedbackSaved(false);
    }
  }, [feedbackPrompt]);

  const openFeedbackModal = useCallback((prompt) => {
    if (!prompt?.attemptId || !prompt?.userId || !prompt?.userPathwayId || !prompt?.projectId) {
      toast.error('No active speech attempt to attach feedback to.', { position: 'bottom-left' });
      return;
    }

    setFeedbackPrompt(prompt);
    setShowFeedbackModal(true);
    loadAttemptFeedbacks(prompt, true);
  }, [loadAttemptFeedbacks]);

  const openEvaluationModal = useCallback((prompt) => {
    if (!prompt?.attemptId || !prompt?.userId || !prompt?.userPathwayId || !prompt?.projectId) {
      toast.error('No active speech attempt to attach evaluation to.', { position: 'bottom-left' });
      return;
    }

    const currentUserName = `${authUser?.user?.firstName || ''} ${authUser?.user?.lastName || ''}`.trim();
    setEvaluationPrompt(prompt);
    setEvaluationEvaluatorName(currentUserName);
    setShowEvaluationModal(true);
    loadManagedEvaluationTopics();
    loadAttemptEvaluations(prompt, true);
  }, [authUser?.user?.firstName, authUser?.user?.lastName, loadAttemptEvaluations, loadManagedEvaluationTopics]);

  const handleEvaluationTopicChange = useCallback((index, field, value) => {
    setEvaluationTopics((prev) => prev.map((topic, topicIndex) => (
      topicIndex === index ? { ...topic, [field]: value } : topic
    )));
  }, []);

  const handleSubmitEvaluation = useCallback(async () => {
    if (!evaluationPrompt?.attemptId) {
      toast.error('No active speech attempt to attach evaluation to.', { position: 'bottom-left' });
      return;
    }

    const derivedEvaluatorName = evaluationEvaluatorName.trim();

    if (!derivedEvaluatorName) {
      toast.error('Please provide evaluator name.', { position: 'bottom-left' });
      return;
    }

    const sanitizedTopics = evaluationTopics.map((topic) => ({
      name: String(topic?.name || '').trim(),
      stars: Number(topic?.stars || 0),
      shortComment: String(topic?.shortComment || '').trim(),
    }));

    if (sanitizedTopics.length === 0) {
      toast.error('No evaluation topics are configured. Please ask an officer to set them up.', { position: 'bottom-left' });
      return;
    }

    const invalidTopic = sanitizedTopics.find((topic) => !topic.name || !Number.isFinite(topic.stars) || topic.stars < 1 || topic.stars > 5);
    if (invalidTopic) {
      toast.error('Please rate all configured topics with 1-5 stars.', { position: 'bottom-left' });
      return;
    }

    setEvaluationSubmitting(true);
    try {
      const storedSubmission = readStoredEvaluationSubmission(evaluationPrompt.attemptId);
      const submissionId = storedSubmission?.submissionId || createSubmissionId();
      const nextEvaluation = {
        submissionId,
        evaluatorName: derivedEvaluatorName,
        feedback: evaluationFeedbackText.trim(),
        topics: sanitizedTopics,
        submittedAt: new Date().toISOString(),
      };

      const response = await fetch(
        `${url}/user/${evaluationPrompt.userId}/userPathway/${evaluationPrompt.userPathwayId}/project/${evaluationPrompt.projectId}/attempt/${evaluationPrompt.attemptId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authUser?.token || ''}`,
          },
          body: JSON.stringify({
            evaluation: nextEvaluation,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.error || errorData?.Error || `Server error ${response.status}`);
      }

      writeStoredEvaluationSubmission(evaluationPrompt.attemptId, {
        submissionId,
        evaluatorName: derivedEvaluatorName,
        feedback: evaluationFeedbackText.trim(),
        topics: sanitizedTopics,
      });
      setEvaluationSavedByAttempt(prev => ({
        ...prev,
        [evaluationPrompt.attemptId]: true,
      }));
      setShowEvaluationModal(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save evaluation.', { position: 'bottom-left' });
    } finally {
      setEvaluationSubmitting(false);
    }
  }, [authUser?.token, evaluationPrompt, evaluationTopics, evaluationEvaluatorName, evaluationFeedbackText, readStoredEvaluationSubmission, url, writeStoredEvaluationSubmission]);

  const parseEvaluationTopicName = useCallback((topicName) => {
    const name = String(topicName || '').trim();
    const match = name.match(/^(.*?)\s*\((.*)\)\s*$/);

    if (!match) {
      return { main: name, details: '' };
    }

    return {
      main: (match[1] || '').trim(),
      details: (match[2] || '').trim(),
    };
  }, []);

  const activePollAgendaItem = activePoll?.title
    ? agendaWithStartTimes.find(item => item?.agendaEvent?.eventType === 'poll' && item?.agendaEvent?.name === activePoll.title)
    : null;

  const roleAssignments = assignments || meeting?.roleAssignments;
  const isPromptEvaluator = (prompt) => !meeting?.meetingDetails?.isContest && isAssignedSpeechEvaluator({
    prompt, items: meeting?.items, assignments: roleAssignments, user: authUser?.user,
  });

  const tableTopicsAvailable = Boolean(
    tableTopics?.active && tableTopics.role === 'evaluator' && authUser && !meeting?.meetingDetails?.isContest && tableTopics.pages?.length
  );
  const tableTopicsSaved = Boolean(tableTopics?.mine?.entries?.length);
  useEffect(() => {
    if (!tableTopicsAvailable) setShowTableTopicsModal(false);
  }, [tableTopicsAvailable]);
  const tableTopicsLabel = `${tableTopicsSaved ? 'Edit ' : ''}Table Topics Evaluation`;

  const participationActions = [];
  if (!isLoading && meeting.items?.length > 0) {
    if (tableTopicsAvailable) {
      participationActions.push({
        key: 'table-topics',
        label: tableTopicsLabel,
        icon: 'bi-chat-quote-fill',
        onClick: () => setShowTableTopicsModal(true),
      });
    }
    feedbackPrompts.forEach(prompt => {
      if (!prompt?.attemptId || !prompt?.userId || !prompt?.userPathwayId || !prompt?.projectId) return;
      const key = prompt.attemptId || prompt.agendaItemId;
      const evaluator = isPromptEvaluator(prompt);
      if (!evaluator && !feedbackExpiredByKey[key]) {
        participationActions.push({
          key: `feedback-${key}`,
          label: `${feedbackSavedByAttempt[key] ? 'Edit speech feedback' : 'Speech Feedback'} - ${prompt.speakerName || 'speaker'}`,
          icon: 'bi-chat-left-text-fill',
          onClick: () => openFeedbackModal(prompt),
        });
      }
      const displayedAt = Number(prompt.displayedAt || 0);
      if (evaluator && displayedAt && Date.now() - displayedAt <= EVALUATION_WINDOW_MS) {
        participationActions.push({
          key: `evaluation-${key}`,
          label: `${evaluationSavedByAttempt[key] ? 'Edit speech evaluation' : 'Speech Evaluation'} - ${prompt.speakerName || 'speaker'}`,
          icon: 'bi-star-fill',
          onClick: () => openEvaluationModal(prompt),
        });
      }
    });
    if (activePollAgendaItem) {
      participationActions.push({
        key: 'vote',
        label: `Vote: ${activePollAgendaItem.agendaEvent?.name || 'Current Poll'}`,
        icon: 'bi-megaphone-fill',
        onClick: () => setSelectedPoll(activePollAgendaItem),
      });
    }
  }
  useNavigationAction('participation', compact && participationActions.length ? {
    label: 'Page Actions',
    icon: 'bi-lightning-fill',
    renderContent: () => (
      <div className="mobile-more-actions">
        {participationActions.map(action => (
          <button type="button" key={action.key} onClick={action.onClick}>
            <i className={`bi ${action.icon}`} aria-hidden="true" />
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    ),
  } : null);


  const onDragEnd = async (result) => {
    const { source, destination } = result;

    // If dropped outside the list or no change in position
    if (!destination || source.index === destination.index) {
      return;
    }

    // Reorder the agenda items
    const reorderedAgenda = Array.from(agendaWithStartTimes);
    const [movedItem] = reorderedAgenda.splice(source.index, 1);
    reorderedAgenda.splice(destination.index, 0, movedItem);

    // Update positions in the backend
    for (let i = 0; i < reorderedAgenda.length; i++) {
      const updatedItem = reorderedAgenda[i];
      if (updatedItem.position !== i + 1) {
        // await agendaItemUpdate(updatedItem.agendaItemId, { position: i + 1 });
        await fetch(`${url}/agendaItem/${updatedItem.agendaItemId}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + authUser.token
          },
          body: JSON.stringify({
            position: i + 1,
          }),
        });

      }

    }
    // Recalculate start times
    const meetingTime = meeting.meetingDetails.meetingTime;
    const updatedAgendaWithStartTimes = calculateStartTime(meetingTime, reorderedAgenda);

    // Update the local state
    setAgendaWithStartTimes(updatedAgendaWithStartTimes);


    // Refresh the meeting data
    getMeeting();
  };

  return (
    <>
      {
        isLoading ?
          <AppLoader />
          :
          <>
            {
              (meeting.items?.length === 0) ?
                <Row className='mt-3 mb-3'>
                  <Col>
                    <h2>There are no items in this agenda</h2>
                    {(authUser && authUser.user.acl === 'Officer' && editMode) &&
                      <AgendaItemAddNewModal meetingId={meetingId} getMeeting={getMeeting} />
                    }
                  </Col>
                </Row>
                :
                <>
                  {!compact && feedbackPrompts.length > 0 && (
                    <Row className="mb-3 justify-content-center">
                      <Col xs={12} className="d-flex justify-content-center flex-wrap gap-2">
                        {feedbackPrompts.map((prompt) => {
                          if (!prompt?.attemptId || !prompt?.userId || !prompt?.userPathwayId || !prompt?.projectId) return null;
                          const promptKey = prompt?.attemptId || prompt?.agendaItemId;
                          const savedForPrompt = Boolean(feedbackSavedByAttempt[promptKey]);
                          const evaluationSavedForPrompt = Boolean(evaluationSavedByAttempt[promptKey]);
                          const evaluator = isPromptEvaluator(prompt);
                          const feedbackButtonHidden = evaluator || Boolean(feedbackExpiredByKey[promptKey]);
                          const displayedAt = Number(prompt?.displayedAt || 0);
                          const evaluationExpired = !evaluator || !displayedAt || (Date.now() - displayedAt) > EVALUATION_WINDOW_MS;
                          if (feedbackButtonHidden && evaluationExpired) return null;
                          return (
                            <div key={`feedback-prompt-${promptKey}`} className="d-flex flex-wrap justify-content-center gap-2">
                              {!feedbackButtonHidden && (
                                <Button
                                  variant={savedForPrompt ? 'warning' : 'info'}
                                  size="lg"
                                  onClick={() => openFeedbackModal(prompt)}
                                >
                                  <i className="bi bi-chat-left-text-fill me-2"></i>
                                  {savedForPrompt
                                    ? `Edit feedback for ${prompt?.speakerName || 'speaker'}`
                                    : `Write feedback to ${prompt?.speakerName || 'speaker'}'s speech`}
                                </Button>
                              )}
                              {!evaluationExpired && (
                                <Button
                                  variant={evaluationSavedForPrompt ? 'success' : 'outline-success'}
                                  size="lg"
                                  onClick={() => openEvaluationModal(prompt)}
                                >
                                  <i className="bi bi-star-fill me-2"></i>
                                  {evaluationSavedForPrompt
                                    ? `Edit evaluation for ${prompt?.speakerName || 'speaker'}`
                                    : `Evaluate ${prompt?.speakerName || 'speaker'}`}
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </Col>
                    </Row>
                  )}
                  {!compact && tableTopicsAvailable && (
                    <Row className="mb-3 justify-content-center">
                      <Col xs="auto" className="d-flex justify-content-center">
                        <Button
                          variant={tableTopicsSaved ? 'success' : 'outline-success'}
                          size="lg"
                          onClick={() => setShowTableTopicsModal(true)}
                        >
                          <i className="bi bi-chat-quote-fill me-2"></i>
                          {tableTopicsLabel}
                        </Button>
                      </Col>
                    </Row>
                  )}
                  {!compact && !tableTopicsAvailable && tableTopics?.reviews?.length > 0 && (
                    <Row className="mb-3 justify-content-center">
                      <Col xs="auto" className="d-flex justify-content-center">
                        <Button
                          variant="outline-primary"
                          size="lg"
                          onClick={() => onOpenTableTopicsResults?.(null)}
                        >
                          <i className="bi bi-eye-fill me-2"></i>
                          View Table Topics Evaluation
                        </Button>
                      </Col>
                    </Row>
                  )}
                  {!compact && activePollAgendaItem && (
                    <Row className="mb-3 justify-content-center">
                      <Col xs="auto" className="d-flex justify-content-center">
                        <Button
                          variant="warning"
                          size="lg"
                          onClick={() => setSelectedPoll(activePollAgendaItem)}
                        >
                          <i className="bi bi-megaphone-fill me-2"></i>
                          Voting: {activePollAgendaItem?.agendaEvent?.name || 'Current Poll'}
                        </Button>
                      </Col>
                    </Row>
                  )}
                  {selectedPoll && (
                    <AgendaItemModal
                      agenda={agendaWithStartTimes}
                      item={selectedPoll}
                      getMeeting={getMeeting}
                      meeting={meeting}
                      showModal={true}
                      handleCloseModal={() => setSelectedPoll(null)}
                      navigationParent={compact ? 'participation' : null}
                    />
                  )}
                  {(authUser && authUser.user.acl === 'Officer' && editMode) &&
                    <Row className='mt-3 mb-3 '>
                      <Col>
                        <AgendaItemAddNewModal meetingId={meetingId} getMeeting={getMeeting} />
                      </Col>
                    </Row>
                  }
                  <Row className='mt-3 mb-3 agenda-items-list-row'>
                    <Col className='agenda-items-list-col'>
                      <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="agenda">
                          {(provided) => (
                            <div className="agenda-items-list" {...provided.droppableProps} ref={provided.innerRef}>
                              {agendaWithStartTimes.map((agendaItem, index) => (
                                authUser && authUser.user.acl === 'Officer' && editMode ? (
                                  <Draggable key={agendaItem.agendaItemId} draggableId={agendaItem.agendaItemId} index={index}>
                                    {(provided) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                      >
                                        <AgendaItemCard
                                          key={`card-${agendaItem.agendaItemId}`}
                                          agenda={agendaWithStartTimes}
                                          agendaItem={agendaItem}
                                          getMeeting={getMeeting}
                                          meeting={meeting}
                                          showClockText={showClockText}
                                          dragHandleProps={provided.dragHandleProps}
                                          editMode={editMode}
                                          tableTopics={tableTopics}
                                          onOpenTableTopicsResults={onOpenTableTopicsResults}
                                        />
                                        <AgendaItemAddNewModal meetingId={meetingId} getMeeting={getMeeting} prevPos={agendaItem.position} />
                                      </div>
                                    )}
                                  </Draggable>
                                ) : (
                                  <div key={agendaItem.agendaItemId}>
                                    <AgendaItemCard
                                      key={`card-${agendaItem.agendaItemId}`}
                                      agenda={agendaWithStartTimes}
                                      agendaItem={agendaItem}
                                      getMeeting={getMeeting}
                                      meeting={meeting}
                                      showClockText={showClockText}
                                      startTime={agendaItem.startTime}
                                      editMode={editMode}
                                      tableTopics={tableTopics}
                                      onOpenTableTopicsResults={onOpenTableTopicsResults}
                                    />
                                  </div>
                                )
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </Col>
                  </Row>

                  <ParticipationModal
                    embedded={compact}
                    show={showFeedbackModal}
                    onHide={() => setShowFeedbackModal(false)}
                    title="Speech Feedback"
                    icon="bi-chat-square-quote-fill"
                    footer={<>
                      <Button variant="outline-secondary" onClick={() => setShowFeedbackModal(false)}>
                        Close
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleSubmitFeedback}
                        disabled={feedbackSubmitting || !feedbackText.trim()}
                      >
                        {feedbackSubmitting
                          ? 'Submitting...'
                          : (feedbackSaved ? 'Update feedback' : 'Save feedback')}
                      </Button>
                    </>}
                  >
                      <div>
                        <p className="mb-2 fw-semibold">
                          {feedbackPrompt?.speakerName
                            ? `Provide feedback for ${feedbackPrompt.speakerName}${feedbackPrompt?.eventName ? ` - ${feedbackPrompt.eventName}` : ''}.`
                            : 'Provide feedback for the speaker.'}
                        </p>
                        <p className="small mb-3" style={{ opacity: 0.85 }}>
                          Share what was effective, what could improve, and any practical suggestions for the attempt.
                        </p>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          placeholder="Write feedback here..."
                          value={feedbackText}
                          onChange={(e) => setFeedbackText(e.target.value)}
                          style={{ minHeight: 140 }}
                        />

                        {/* Intentionally only showing the editor in this modal. */}
                      </div>
                  </ParticipationModal>

                  <ParticipationModal
                    embedded={compact}
                    show={showEvaluationModal}
                    onHide={() => setShowEvaluationModal(false)}
                    title="Speech Evaluation"
                    icon="bi-stars"
                    footer={<>
                      <Button variant="outline-secondary" onClick={() => setShowEvaluationModal(false)}>
                        Close
                      </Button>
                      <Button
                        variant="success"
                        onClick={handleSubmitEvaluation}
                        disabled={evaluationSubmitting}
                      >
                        {evaluationSubmitting ? 'Submitting...' : 'Save evaluation'}
                      </Button>
                    </>}
                  >
                      <div>
                        <p className="mb-2 fw-semibold">
                          {evaluationPrompt?.speakerName
                            ? `Evaluate ${evaluationPrompt.speakerName}${evaluationPrompt?.eventName ? ` - ${evaluationPrompt.eventName}` : ''}.`
                            : 'Evaluate the speaker.'}
                        </p>
                        <p className="small mb-3" style={{ opacity: 0.85 }}>
                          Rate each configured topic with 1-5 stars. You can include short comments for every topic.
                        </p>

                        <Form.Group className="mb-3 text-start">
                          <Form.Label>Evaluator name</Form.Label>
                          <Form.Control
                            type="text"
                            value={evaluationEvaluatorName}
                            onChange={(e) => setEvaluationEvaluatorName(e.target.value)}
                            placeholder="Enter your name"
                          />
                        </Form.Group>

                        <Form.Group className="text-start mb-3">
                          <Form.Label className="mb-1">Feedback</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={14}
                            maxLength={2000}
                            value={evaluationFeedbackText}
                            onChange={(e) => setEvaluationFeedbackText(e.target.value)}
                            placeholder="Write your overall feedback"
                          />
                        </Form.Group>

                        <div className="d-flex flex-column gap-3">
                          {evaluationTopics.map((topic, index) => (
                            <div key={`evaluation-topic-${index}`} className="border rounded p-3 text-start">
                              {(() => {
                                const parsedTopic = parseEvaluationTopicName(topic.name);
                                return (
                                  <div className="mb-2">
                                    <div className="fw-bold">{parsedTopic.main}</div>
                                    {parsedTopic.details && (
                                      <div className="small" style={{ opacity: 0.85 }}>({parsedTopic.details})</div>
                                    )}
                                  </div>
                                );
                              })()}

                              <Form.Group className="mb-2">
                                <div className="d-flex gap-1 flex-wrap">
                                  {[1, 2, 3, 4, 5].map((starValue) => (
                                    <Button
                                      key={`topic-${index}-star-${starValue}`}
                                      size="sm"
                                      variant="link"
                                      className="p-0"
                                      style={{ textDecoration: 'none', border: 'none', boxShadow: 'none' }}
                                      onClick={() => handleEvaluationTopicChange(index, 'stars', starValue)}
                                      title={`${starValue} star${starValue > 1 ? 's' : ''}`}
                                    >
                                      <i
                                        className={Number(topic.stars) >= starValue ? 'bi bi-star-fill' : 'bi bi-star'}
                                        style={{
                                          fontSize: '1.2rem',
                                          color: Number(topic.stars) >= starValue ? '#ffc107' : 'var(--foreground)',
                                        }}
                                      ></i>
                                    </Button>
                                  ))}
                                </div>
                              </Form.Group>

                              <Form.Group className="mb-2">
                                <Form.Label className="mb-1">Short comment (optional)</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={2}
                                  maxLength={300}
                                  value={topic.shortComment}
                                  onChange={(e) => handleEvaluationTopicChange(index, 'shortComment', e.target.value)}
                                  placeholder="Optional short comment"
                                />
                              </Form.Group>
                            </div>
                          ))}
                        </div>
                      </div>
                  </ParticipationModal>
                  {tableTopics && (
                    <TableTopicsReviewForm
                      embedded={compact}
                      show={showTableTopicsModal && tableTopicsAvailable}
                      onHide={() => setShowTableTopicsModal(false)}
                      tableTopics={tableTopics}
                      items={meeting?.items}
                    />
                  )}
                </>
            }
          </>
      }
    </>
  );
}
